// Core types for the unified API service
import type { AxiosRequestConfig } from "axios";

// ============================================================================
// Core Infrastructure Types
// ============================================================================

export interface ApiError {
  message: string;
  code: string;
  status?: number;
  details?: unknown;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface BulkOperationResult {
  success_count: number;
  failed_count: number;
  errors: string[];
}

export interface RequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  retryCount?: number;
  timeout?: number;
}

export interface ProgressCallback {
  (progress: number): void;
}

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  retryCondition: (error: ApiError) => boolean;
}

// Response wrapper types
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T = unknown> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export interface MessageResponse {
  message: string;
}

// ============================================================================
// Core Data Models (matching Rust backend)
// ============================================================================

// User and Authentication Types
export interface User {
  id: string;
  email: string;
  name?: string;
  avatar?: string;
  provider?: string;
  provider_id?: string;
  created_at?: string;
  role?: "owner" | "hr_lead" | "recruiter" | "panelist";
  permissions?: string[];
}

export interface UserWithAdmin extends User {
  is_admin: boolean;
}

export interface Profile {
  userId: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  location?: string;
  bio?: string;
  website?: string;
  linkedinUrl?: string;
  githubUrl?: string;
  skills: string[];
  resumeStatus?: string;
  lastResumeId?: string;
  updatedAt?: string;
}

export interface ProfileResponse {
  profile: Profile;
  user: User;
}

// Job Types
export interface Job {
  educational_qualifications?: EducationalQualification[];
  id: string;
  title: string;
  summary?: string; // Short AI-generated summary for card previews
  description?: string;
  location?: string;
  company?: string;
  company_id?: string;
  company_logo_url?: string;
  job_image_url?: string;
  salary_min?: number;
  salary_max?: number;
  job_type?: string; // full-time, part-time, contract, etc.
  experience_level?: string; // entry, mid, senior, etc.
  requirements?: string[]; // Array of requirement strings
  benefits?: string[]; // Array of benefit strings
  status?: string; // draft, active, inactive, closed
  is_featured?: boolean | number; // Featured status (backend returns 0/1)
  created_at?: string;
  updated_at?: string;
  published_at?: string;
  template_id?: string;
  postedDate?: string;
  applicationDeadline?: string;
  type?: string;
  experience?: string;
  featured?: boolean;
  companyLogo?: string;
  companyDescription?: string;
  salary?: string;
  viewCount?: number;
  applicationCount?: number;
  isBookmarked?: boolean;
  hasApplied?: boolean;
}

export interface CreateJob {
  title: string;
  description?: string;
  location?: string;
  company?: string;
  company_logo_url?: string;
  job_image_url?: string;
  salary_min?: number;
  salary_max?: number;
  job_type?: string;
  experience_level?: string;
  requirements?: string[];
  benefits?: string[];
  educational_qualifications?: EducationalQualification[];
  status?: string;
}

export interface UpdateJob {
  title?: string;
  description?: string;
  location?: string;
  company?: string;
  company_logo_url?: string;
  job_image_url?: string;
  salary_min?: number;
  salary_max?: number;
  job_type?: string;
  experience_level?: string;
  requirements?: string[];
  benefits?: string[];
  status?: string;
}

// Enhanced Job Types (kept for backward compatibility)
export type EnhancedJob = Job;

export type CreateEnhancedJobRequest = CreateJob;

export type UpdateEnhancedJobRequest = UpdateJob;

// Resume Types
export interface Resume {
  id: string;
  user_id: string;
  filename: string;
  label?: string;
  status: string;
  score?: number;
  parsed_json?: string;
  file_size?: number;
  submitted_at?: string;
  processed_at?: string;
  deleted_at?: string;
  // Candidate information (populated in admin queries)
  candidate_name?: string;
  candidate_email?: string;
  userId?: string;
  originalFilename?: string;
  fileSize?: number;
  submittedAt?: string;
  processedAt?: string;
  fileType?: string;
  parsedData?: ParsedResumeData;
  errorMessage?: string;
}

export interface ParsedResumeData {
  name?: string;
  email?: string;
  phone?: string;
  skills: string[];
  experience: Array<{
    company?: string;
    title?: string;
    startDate?: string;
    endDate?: string;
    description: string;
    location?: string;
  }>;
  education: Array<{
    institution?: string;
    degree?: string;
    field?: string;
    startDate?: string;
    endDate?: string;
    gpa?: string;
  }>;
  summary?: string;
  strengths?: string[];
  areas_for_improvement?: string[];
  score_breakdown?: {
    skills_match?: number;
    experience_quality?: number;
    education?: number;
    presentation?: number;
  };
}

export interface ResumeUploadResponse {
  id: string;
  filename: string;
  status: string;
  message?: string;
}

export interface ResumeWithCandidate extends Resume {
  // candidate_name and candidate_email are now in Resume base interface
  candidate_phone?: string;
  candidate_location?: string;
  processing_metadata?: ResumeProcessingMetadata;
}

export interface ResumeProcessingMetadata {
  processing_started_at?: string;
  processing_completed_at?: string;
  processing_duration_ms?: number;
  error_message?: string;
  retry_count: number;
  last_retry_at?: string;
}

export interface DetailedResumeStatus {
  resume_id: string;
  status: string;
  progress_percentage: number;
  current_step: string;
  error_messages: string[];
  estimated_completion_time?: string;
  processing_metadata: ResumeProcessingMetadata;
  id?: string;
  progress?: number;
  stage?: string;
  errorMessage?: string;
}

// Video Submission Types
export interface VideoSubmission {
  id: string;
  application_id: string;
  s3_url: string;
  filename: string;
  file_size: number;
  duration_seconds: number;
  mime_type: string;
  uploaded_at?: string;
}

export interface VideoUploadResponse {
  id: string;
  s3_url: string;
  filename: string;
  file_size: number;
  duration_seconds: number;
}

export interface VideoMetadataResponse {
  id: string;
  application_id: string;
  filename: string;
  file_size: number;
  duration_seconds: number;
  mime_type: string;
  video_url: string;
  uploaded_at?: string;
}

export interface VideoUploadProgress {
  percentage: number;
  loaded: number;
  total: number;
}

// Experience and Education Types
export interface Experience {
  id: string;
  user_id: string;
  company: string;
  title: string;
  start_date: string;
  end_date?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Education {
  id: string;
  user_id: string;
  institution: string;
  degree: string;
  field_of_study?: string;
  start_date: string;
  end_date?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

// Application Types
export interface Application {
  current_stage?: string;
  has_video?: boolean;
  id: string;
  user_id: string;
  job_id: string;
  resume_id?: string;
  status: string;
  cover_letter?: string;
  applied_at: string;
  updated_at?: string;
  userId?: string;
  jobId?: string;
  resumeId?: string;
  coverLetter?: string;
  appliedAt?: string;
  updatedAt?: string;
}

export interface ApplicationStatusHistory {
  id: string;
  application_id: string;
  status: string;
  changed_by: string;
  notes?: string;
  changed_at?: string;
}

export interface ApplicationWithDetails extends Application {
  job_title?: string;
  job_company?: string;
  job_location?: string;
  job_salary_min?: number;
  job_salary_max?: number;
  job_image_url?: string;
  company_logo_url?: string;
  resume_filename?: string;
  resume_label?: string;
  candidate_name?: string;
  candidate_email?: string;
  status_history: ApplicationStatusHistory[];
  jobTitle?: string;
  jobCompany?: string;
  jobLocation?: string;
  resumeFilename?: string;
  resumeLabel?: string;
  statusHistory?: ApplicationStatusHistory[];
}

export interface JobApplicationDetails {
  application_id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  resume_id?: string;
  resume_filename?: string;
  resume_label?: string;
  status: string;
  applied_at: string;
  cover_letter?: string;
}

export interface EmailHistoryEntry {
  id: string;
  application_id: string;
  candidate_id: string;
  job_id: string;
  subject: string;
  content: string;
  cc?: string | null;
  sent_by: string;
  sent_at?: string;
  email_type?: string | null;
}

// Conversation and Message Types
export interface ConversationMessage {
  id: string;
  user_id: string;
  sender: string;
  message: string;
  created_at?: string;
}

export interface ConversationInput {
  message: string;
}

export interface MessageAttachment {
  id: string;
  message_id: string;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  file_path: string;
  created_at?: string;
}

export interface ConversationWithMetadata {
  id: string;
  participants: ConversationParticipant[];
  last_message?: EnhancedConversationMessage;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface ConversationParticipant {
  user_id: string;
  name?: string;
  email: string;
  avatar?: string;
  role: string; // user, admin
}

export interface EnhancedConversationMessage {
  id: string;
  user_id: string;
  sender: string;
  message: string;
  attachments: MessageAttachment[];
  is_read: boolean;
  created_at?: string;
}

// Job Analytics Types
export interface JobView {
  id: string;
  job_id: string;
  user_id?: string;
  ip_address?: string;
  user_agent?: string;
  viewed_at?: string;
}

export interface DailyMetric {
  date: string;
  count: number;
}

export interface ReferrerStats {
  referrer: string;
  count: number;
}

export interface JobStats {
  job_id: string;
  job_title: string;
  total_views: number;
  unique_views: number;
  total_applications: number;
  conversion_rate: number;
  recent_views: JobView[];
  view_trend: DailyMetric[];
  application_trend: DailyMetric[];
}

export interface JobAnalytics {
  job_id: string;
  job_title: string;
  views: number;
  applications: number;
  conversion_rate: number;
  view_trend: DailyMetric[];
  application_trend: DailyMetric[];
  top_referrers: ReferrerStats[];
}

export interface JobAnalyticsResponse {
  total_jobs: number;
  total_views: number;
  total_applications: number;
  average_conversion_rate: number;
  top_performing_jobs: JobPerformanceStats[];
  view_trends: DailyMetric[];
  application_trends: DailyMetric[];
}

export interface JobPerformanceStats {
  job_id: string;
  job_title: string;
  views: number;
  applications: number;
  conversion_rate: number;
  created_at?: string;
}

export interface JobApplicationStats {
  job_id: string;
  job_title: string;
  application_count: number;
  latest_application?: string;
}

export interface ConversionRates {
  submitted_to_reviewed: number;
  reviewed_to_interviewed: number;
  interviewed_to_offered: number;
  offered_to_hired: number;
}

export interface ApplicationAnalytics {
  total_applications: number;
  applications_by_status: Record<string, number>;
  applications_by_job: JobApplicationStats[];
  conversion_rates: ConversionRates;
  recent_applications: Application[];
}

// Admin and System Types
export interface DashboardMetrics {
  total_jobs: number;
  active_jobs: number;
  total_applications: number;
  pending_reviews: number;
  new_messages: number;
  total_candidates: number;
  system_health: string;
  last_updated: string;
}

export interface SystemHealth {
  database_status: string;
  api_status: string;
  storage_status: string;
  overall_health: string;
  last_check: string;
  details: Record<string, string>;
}

export interface ActivityLog {
  id: string;
  activity_type: string;
  description: string;
  user_id?: string;
  user_email?: string;
  metadata?: string;
  timestamp: string;
}

export interface CandidateProfile {
  user: User;
  profile?: Profile;
  experiences: Experience[];
  education: Education[];
  applications: Application[];
  resume_status: string;
  resume_id?: string;
  resume_filename?: string;
  last_activity?: string;
  total_applications: number;
  application_success_rate: number;
}

export type TeamRole = "owner" | "hr_lead" | "recruiter" | "panelist";
export type TeamMemberStatus = "invited" | "active" | "disabled";

export interface TeamSubject {
  id: string;
  name: string;
  is_active: number;
  created_at: string;
}

export interface TeamMember {
  id: string;
  user_id?: string;
  email: string;
  name?: string;
  role: TeamRole;
  permissions: string[];
  status: TeamMemberStatus;
  invited_by?: string;
  subjects: TeamSubject[];
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

export interface TeamInvitation {
  id: string;
  team_member_id: string;
  email: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  expires_at: string;
  invited_by: string;
  accepted_at?: string;
  created_at: string;
}

export interface CreateTeamInvitationRequest {
  email: string;
  name?: string;
  role: Exclude<TeamRole, "owner">;
  subject_ids: string[];
}

export interface CreateTeamInvitationResponse {
  invitation: TeamInvitation;
  member: TeamMember;
  email_sent: boolean;
}

export interface InvitationPreview {
  email: string;
  name?: string;
  role: TeamRole;
  status: string;
  expires_at: string;
  subjects: TeamSubject[];
}

export interface PanelAssignment {
  assignment_id: string;
  interview_id: string;
  assignment_role: "lead" | "panelist";
  assignment_status: "assigned" | "accepted" | "declined" | "completed";
  subject_id?: string;
  subject_name?: string;
  scheduled_date: string;
  duration_minutes: number;
  interview_type: string;
  google_meet_link?: string;
  interview_notes?: string;
  candidate_name: string;
  job_title: string;
  application_id: string;
  resume_id?: string;
  cover_letter?: string;
  feedback_id?: string;
  recommendation?: string;
  rating?: number;
  strengths?: string;
  concerns?: string;
  private_notes?: string;
  submitted_at?: string;
}

export interface SubmitPanelFeedbackRequest {
  recommendation: "strong_yes" | "yes" | "mixed" | "no" | "strong_no";
  rating: number;
  strengths: string;
  concerns: string;
  private_notes?: string;
}

export interface SystemSetting {
  key: string;
  value: string;
  description?: string;
  updated_at?: string;
  updated_by?: string;
}

// ============================================================================
// Authentication Types
// ============================================================================

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface GoogleIdTokenPayload {
  id_token: string;
  invitation_token?: string;
}

export interface AuthResponse {
  is_admin?: boolean;
  is_new_user?: boolean;
  token: string;
  user: User;
}

// ============================================================================
// File Upload Types
// ============================================================================

export interface FileUploadConfig extends RequestConfig {
  onProgress?: ProgressCallback;
  maxFileSize?: number;
  allowedTypes?: string[];
}

export interface FileUploadResponse {
  url: string;
  filename: string;
  size: number;
  type: string;
}

export interface ImageUploadResponse {
  image_url: string;
  url?: string;
  message?: string;
  filename: string;
  file_size: number;
  mime_type: string;
}

export interface AvatarUploadResponse {
  avatar_url: string;
  message: string;
}

export interface AvatarUpdateRequest {
  avatar_url: string;
}

// ============================================================================
// Request Types (matching backend expectations)
// ============================================================================

// Profile Management Requests
export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  location?: string;
  bio?: string;
  website?: string;
  linkedinUrl?: string;
  githubUrl?: string;
  skills?: string[];
}

export interface CreateExperienceRequest {
  company: string;
  title: string;
  start_date: string;
  end_date?: string;
  description?: string;
}

export interface UpdateExperienceRequest {
  company?: string;
  title?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
}

export interface CreateEducationRequest {
  institution: string;
  degree: string;
  field_of_study?: string;
  start_date: string;
  end_date?: string;
  description?: string;
}

export interface UpdateEducationRequest {
  institution?: string;
  degree?: string;
  field_of_study?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
}

// Application Management Requests
export interface CreateApplicationRequest {
  job_id: string;
  resume_id?: string;
  cover_letter?: string;
}

export interface UpdateApplicationStatusRequest {
  status: string;
  notes?: string;
}

// Job Analytics Requests
export interface JobAnalyticsRequest {
  job_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface JobViewRequest {
  user_agent?: string;
  referrer?: string;
}

// Messaging Requests
export interface CreateMessageRequest {
  message: string;
  conversation_id?: string;
  recipient_id?: string;
}

export interface MessageSearchRequest {
  query: string;
  conversation_id?: string;
  limit?: number;
  offset?: number;
}

export interface MarkMessageReadRequest {
  message_ids: string[];
}

export interface MarkConversationReadRequest {
  conversation_id: string;
}

export interface MessageSearchResult {
  messages: EnhancedConversationMessage[];
  total_count: number;
  has_more: boolean;
}

export interface SettingUpdate {
  value: string;
  encrypt?: boolean;
}

export interface UpdateSystemSettingsRequest {
  settings: Record<string, SettingUpdate>;
}

// Resume Processing Requests
export interface AdminResumeFilters {
  status?: string;
  candidate_name?: string;
  date_from?: string;
  date_to?: string;
  score_min?: number;
  score_max?: number;
  page?: number;
  limit?: number;
  sort_by?: string; // submitted_at, score, status, candidate_name
  sort_order?: string; // asc, desc
}

export interface BulkResumeStatusUpdate {
  resume_ids: string[];
  status: string;
  notes?: string;
}

export interface RetryResumeProcessingRequest {
  force_reprocess?: boolean;
  priority?: string; // high, normal, low
}

export interface UpdateResumeLabelRequest {
  label: string;
}

// Bulk Operations Requests
export interface BulkJobStatusUpdate {
  job_ids: string[];
  status: string;
}

export interface BulkJobDelete {
  job_ids: string[];
}

export interface BulkApplicationStatusUpdate {
  application_ids: string[];
  status: string;
  notes?: string;
}

// Export Requests
export interface ExportRequest {
  format: string; // csv, json
  filters?: Record<string, string>;
  fields?: string[];
}

export interface ExportResponse {
  download_url: string;
  filename: string;
  record_count: number;
  expires_at: string;
}

// ============================================================================
// Union Types and Enums
// ============================================================================

export type JobStatus = "active" | "inactive" | "closed";
export type JobType = "full-time" | "part-time" | "contract" | "remote";
export type ExperienceLevel = "entry" | "mid" | "senior" | "executive";
export type ResumeStatus = "submitted" | "processing" | "scanned" | "error";
export type ApplicationStatus =
  | "submitted"
  | "reviewed"
  | "shortlisted"
  | "interview_scheduled"
  | "interviewed"
  | "offered"
  | "hired"
  | "rejected"
  | "withdrawn";
export type UserRole = "user" | "admin";
export type SystemHealthStatus = "healthy" | "degraded" | "unhealthy";
export type ExportFormat = "csv" | "json";
export type SortOrder = "asc" | "desc";
export type ProcessingPriority = "high" | "normal" | "low";

// ============================================================================
// Validation and Error Types
// ============================================================================

export interface ValidationResult {
  is_valid: boolean;
  errors: ValidationError[];
}

export interface ErrorResponse {
  error: string;
  code: string;
}

// ============================================================================
// Utility Types for Date Handling
// ============================================================================

export interface DateRange {
  start_date?: string;
  end_date?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: SortOrder;
}

export interface SearchParams {
  query?: string;
  filters?: Record<string, unknown>;
}

// ============================================================================
// Type Guards and Validation Helpers
// ============================================================================

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const isValidDateString = (date: string): boolean => {
  return !isNaN(Date.parse(date));
};

export const isResumeStatus = (status: string): status is ResumeStatus => {
  return ["submitted", "processing", "scanned", "error"].includes(status);
};

export const isApplicationStatus = (
  status: string,
): status is ApplicationStatus => {
  return [
    "submitted",
    "reviewed",
    "shortlisted",
    "interview_scheduled",
    "interviewed",
    "offered",
    "hired",
    "rejected",
    "withdrawn",
  ].includes(status);
};

export const isJobStatus = (status: string): status is JobStatus => {
  return ["active", "inactive", "closed"].includes(status);
};

// ============================================================================
// Serialization/Deserialization Helpers
// ============================================================================

export const serializeDate = (date: Date): string => {
  return date.toISOString();
};

export const deserializeDate = (dateString: string): Date => {
  return new Date(dateString);
};

export const serializeSkills = (skills: string[]): string => {
  return JSON.stringify(skills);
};

export const deserializeSkills = (skillsJson: string): string[] => {
  try {
    return JSON.parse(skillsJson);
  } catch {
    return [];
  }
};

export const serializeRequirements = (requirements: string[]): string => {
  return JSON.stringify(requirements);
};

export const deserializeRequirements = (requirementsJson: string): string[] => {
  try {
    return JSON.parse(requirementsJson);
  } catch {
    return [];
  }
};

export const serializeBenefits = (benefits: string[]): string => {
  return JSON.stringify(benefits);
};

export const deserializeBenefits = (benefitsJson: string): string[] => {
  try {
    return JSON.parse(benefitsJson);
  } catch {
    return [];
  }
};

export const serializePermissions = (permissions: string[]): string => {
  return JSON.stringify(permissions);
};

export const deserializePermissions = (permissionsJson: string): string[] => {
  try {
    return JSON.parse(permissionsJson);
  } catch {
    return [];
  }
};

// ============================================================================
// Additional Request/Response Type Pairs for Specific Endpoints
// ============================================================================

// Authentication Response Types
export type LoginResponse = AuthResponse;

export interface LogoutResponse {
  message: string;
}

export type MeResponse = UserWithAdmin;

// Job Response Types
export interface JobListResponse {
  jobs: Job[];
  total: number;
  page?: number;
  page_size?: number;
  totalPages?: number;
  hasMore?: boolean;
}

export type CreateJobResponse = Job;

export type UpdateJobResponse = Job;

export type DeleteJobResponse = MessageResponse;

// Profile Response Types
export type GetProfileResponse = ProfileResponse;

export type UpdateProfileResponse = Profile;

// Experience Response Types
export type ExperienceListResponse = Experience[];

export type CreateExperienceResponse = Experience;

export type UpdateExperienceResponse = Experience;

export type DeleteExperienceResponse = MessageResponse;

// Education Response Types
export type EducationListResponse = Education[];

export type CreateEducationResponse = Education;

export type UpdateEducationResponse = Education;

export type DeleteEducationResponse = MessageResponse;

// Application Response Types
export type ApplicationListResponse = Application[];

export type CreateApplicationResponse = Application;

export type ApplicationDetailsResponse = ApplicationWithDetails;

export type UpdateApplicationStatusResponse = Application;

// Resume Response Types
export type ResumeListResponse = Resume[];

export type ScanResumeResponse = MessageResponse;

export type AdminResumeListResponse = ResumeWithCandidate[];

export type DownloadResumeResponse = Blob;

export type RetryResumeProcessingResponse = MessageResponse;

// Conversation Response Types
export type ConversationListResponse = ConversationMessage[];

export type SendConversationResponse = MessageResponse;

export type ConversationDetailsResponse = ConversationWithMetadata;

export type ConversationMessagesResponse = EnhancedConversationMessage[];

export type SendMessageWithAttachmentsResponse = MessageResponse;

export type ServeAttachmentResponse = Blob;

export type MarkMessageReadResponse = MessageResponse;

export type MarkConversationReadResponse = MessageResponse;

// Avatar Response Types
export type UploadAvatarResponse = AvatarUploadResponse;

export type UpdateAvatarUrlResponse = AvatarUploadResponse;

export type RemoveAvatarResponse = MessageResponse;

export type ServeAvatarResponse = Blob;

// Admin Response Types
export type AdminCandidateListResponse = CandidateProfile[];

export type AdminCandidateDetailsResponse = CandidateProfile;

export type SystemSettingsListResponse = SystemSetting[];

export type UpdateSystemSettingsResponse = MessageResponse;

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  service: string;
}

// Analytics Response Types
export type GetJobAnalyticsResponse = JobAnalyticsResponse;

export type TrackJobViewResponse = MessageResponse;

export type GetJobStatsResponse = JobStats;

export type GetApplicationAnalyticsResponse = ApplicationAnalytics;

export type GetDashboardMetricsResponse = DashboardMetrics;

export type GetSystemHealthResponse = SystemHealth;

export type GetRecentActivityResponse = ActivityLog[];

// Bulk Operations Response Types
export type BulkUpdateJobStatusResponse = BulkOperationResult;

export type BulkDeleteJobsResponse = BulkOperationResult;

export type BulkUpdateApplicationStatusResponse = BulkOperationResult;

export type BulkUpdateResumeStatusResponse = BulkOperationResult;

// Export Response Types
export type ExportJobsResponse = ExportResponse;

export type ExportApplicationsResponse = ExportResponse;

export type ExportCandidatesResponse = ExportResponse;

// ============================================================================
// Query Parameter Types for GET Endpoints
// ============================================================================

export type JobAnalyticsQueryParams = JobAnalyticsRequest;

export interface MessageSearchQueryParams
  extends Omit<MessageSearchRequest, "query"> {
  q: string; // URL query param is typically 'q' instead of 'query'
}

export interface ConversationMessagesQueryParams {
  page?: number;
  limit?: number;
}

export type AdminResumeQueryParams = AdminResumeFilters;

// ============================================================================
// Form Data Types for Multipart Requests
// ============================================================================

export interface ResumeUploadFormData {
  file: File;
  user_id?: string;
}

export interface AvatarUploadFormData {
  file: File;
}

export interface MessageWithAttachmentsFormData {
  message: string;
  conversation_id?: string;
  recipient_id?: string;
  attachments?: FileList;
}

// ============================================================================
// WebSocket/Real-time Types (for future use)
// ============================================================================

export interface WebSocketMessage {
  type: string;
  payload: unknown;
  timestamp: string;
}

export interface NotificationMessage extends WebSocketMessage {
  type: "notification";
  payload: {
    title: string;
    message: string;
    level: "info" | "warning" | "error" | "success";
  };
}

export interface MessageNotification extends WebSocketMessage {
  type: "message";
  payload: {
    conversation_id: string;
    message: EnhancedConversationMessage;
  };
}

export interface ApplicationStatusNotification extends WebSocketMessage {
  type: "application_status";
  payload: {
    application_id: string;
    old_status: ApplicationStatus;
    new_status: ApplicationStatus;
    notes?: string;
  };
}

export interface ResumeProcessingNotification extends WebSocketMessage {
  type: "resume_processing";
  payload: {
    resume_id: string;
    status: ResumeStatus;
    progress_percentage: number;
    current_step: string;
  };
}

// ============================================================================
// Cache Key Types for React Query
// ============================================================================

export type QueryKey =
  | ["auth", "me"]
  | ["jobs"]
  | ["jobs", string] // job by id
  | ["jobs", "analytics"]
  | ["jobs", string, "stats"] // job stats by id
  | ["profile"]
  | ["profile", "experience"]
  | ["profile", "education"]
  | ["applications"]
  | ["applications", string] // application by id
  | ["resumes"]
  | ["resumes", string] // resume by id
  | ["resumes", string, "status"] // resume status by id
  | ["conversations"]
  | ["conversations", string] // conversation by id
  | ["conversations", string, "messages"] // conversation messages by id
  | ["messages", "search", string] // message search by query
  | ["admin", "dashboard", "metrics"]
  | ["admin", "system", "health"]
  | ["admin", "activity"]
  | ["admin", "candidates"]
  | ["admin", "candidates", string] // candidate by id
  | ["admin", "users"]
  | ["admin", "resumes"]
  | ["admin", "settings"]
  | ["admin", "analytics", "applications"];

// ============================================================================
// HTTP Method Types
// ============================================================================

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// ============================================================================
// Content Type Constants
// ============================================================================

export const CONTENT_TYPES = {
  JSON: "application/json",
  FORM_DATA: "multipart/form-data",
  URL_ENCODED: "application/x-www-form-urlencoded",
  TEXT: "text/plain",
  HTML: "text/html",
  PDF: "application/pdf",
  IMAGE_JPEG: "image/jpeg",
  IMAGE_PNG: "image/png",
  IMAGE_GIF: "image/gif",
  IMAGE_WEBP: "image/webp",
} as const;

export type ContentType = (typeof CONTENT_TYPES)[keyof typeof CONTENT_TYPES];

// ============================================================================
// AI Generation Types
// ============================================================================

export type EmailTemplate =
  | "job_offer"
  | "interview_invitation"
  | "rejection"
  | "follow_up"
  | "general_inquiry";
export type MessageTone = "professional" | "friendly" | "formal" | "empathetic";
export type JobDescriptionTone =
  | "professional"
  | "casual"
  | "enthusiastic"
  | "technical";
export type ImageStyle =
  | "professional"
  | "modern"
  | "creative"
  | "minimalist"
  | "vibrant";
export type SocialPlatform = "linkedin" | "instagram" | "facebook";

export interface GenerateEmailRequest {
  template: EmailTemplate;
  context: string;
  recipient_name?: string;
  job_title?: string;
  company_name?: string;
}

export interface GenerateEmailResponse {
  content: string;
  subject?: string;
}

export interface SuggestResponseRequest {
  conversation_history: Array<{
    sender: string;
    message: string;
  }>;
  tone: MessageTone;
  candidate_name?: string;
  candidate_profile?: string;
  application_status?: string;
}

export interface SuggestResponseResponse {
  response: string;
}

export interface GenerateJobDescriptionRequest {
  job_title: string;
  requirements?: string[];
  experience_level?: string;
  company_name?: string;
}

export interface GenerateJobDescriptionResponse {
  description: string;
  requirements: string[];
  responsibilities: string[];
}

export interface GenerateOfferLetterRequest {
  candidate_name: string;
  job_title: string;
  salary: number;
  start_date: string;
  benefits?: string;
  additional_terms?: string;
  company_name?: string;
}

export interface GenerateOfferLetterResponse {
  content: string;
}

export interface GenerateSocialImagesRequest {
  job_id: string;
  job_title: string;
  company_name?: string;
  platforms: SocialPlatform[];
  style: ImageStyle;
  custom_prompt?: string;
}

export interface GeneratedImage {
  platform: SocialPlatform;
  url: string;
  dimensions: {
    width: number;
    height: number;
  };
}

export interface GenerateSocialImagesResponse {
  images: GeneratedImage[];
}

// ============================================================================
// Interview Scheduling Types (Enhanced)
// ============================================================================

export interface ScheduleInterviewRequest {
  candidate_id: string;
  job_id: string;
  scheduled_at: string;
  duration_minutes: number;
  interviewer_ids: string[];
  create_meet_link: boolean;
  notes?: string;
}

export interface Interview {
  id: string;
  application_id: string;
  candidate_id?: string;
  job_id?: string;
  scheduled_date: string;
  scheduled_at?: string; // alias for scheduled_date
  duration_minutes: number;
  interview_type: string;
  google_meet_link?: string;
  meet_link?: string; // alias for google_meet_link
  google_calendar_event_id?: string;
  calendar_event_id?: string; // alias for google_calendar_event_id
  panel_members?: string;
  notes?: string;
  status?: string;
  created_by: string;
  created_at?: string;
  updated_at?: string;
}

export type ScheduleInterviewResponse = Interview;

export type InterviewListResponse = Interview[];

// ============================================================================
// Company Management Types (NEW)
// ============================================================================

export interface CompanyLocation {
  city: string;
  state?: string;
  country: string;
  address?: string;
  office_type?: string;
}

export interface CompanyCulture {
  values?: string[];
  work_environment?: string;
  team_size?: string;
  perks?: string[];
}

export interface Company {
  id: string;
  name: string;
  description?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  founded_year?: number;
  headquarters?: CompanyLocation | string;
  operating_locations?: CompanyLocation[] | string;
  culture?: CompanyCulture | string;
  benefits?: string[] | string;
  default_logo_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CompanyAsset {
  id: string;
  company_id: string;
  asset_type: "logo" | "image";
  url: string;
  filename: string;
  file_size: number;
  mime_type: string;
  is_default: boolean;
  created_at?: string;
}

export interface CreateCompanyRequest {
  name: string;
  description?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  founded_year?: number;
  headquarters?: CompanyLocation;
  operating_locations?: CompanyLocation[];
  culture?: CompanyCulture;
  benefits?: string[];
  default_logo_url?: string;
}

export interface UpdateCompanyRequest {
  name?: string;
  description?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  founded_year?: number;
  headquarters?: CompanyLocation;
  operating_locations?: CompanyLocation[];
  culture?: CompanyCulture;
  benefits?: string[];
  default_logo_url?: string;
}

export type CompanyListResponse = Company[];
export type CompanyAssetsResponse = CompanyAsset[];

// ============================================================================
// Job Template Types (NEW)
// ============================================================================

/**
 * AI Template Context - Contains context prompts for AI generation
 * Used in AI templates to guide content generation for each job field
 */
export interface AITemplateContext {
  title_context?: string;
  description_context?: string;
  requirements_context?: string;
  benefits_context?: string;
  educational_qualifications_context?: string;
}

/**
 * Template type classification
 * - "system": Pre-defined system templates (read-only)
 * - "custom": User-created templates with static content
 * - "ai": AI-powered templates with context prompts for generation
 */
export type JobTemplateType = "system" | "custom" | "ai";

export interface JobTemplate {
  id: string;
  name: string;
  company_id?: string; // Link to company (required for AI templates)
  template_type: JobTemplateType;
  job_data: CreateJob;
  ai_context?: AITemplateContext; // Context prompts for AI templates
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateJobTemplateRequest {
  name: string;
  company_id?: string; // Link to company (required for AI templates)
  template_type?: JobTemplateType; // Defaults to 'custom' if not specified
  job_data?: CreateJob; // Optional for AI templates
  ai_context?: AITemplateContext; // Required for AI templates
}

export interface UpdateJobTemplateRequest {
  name?: string;
  job_data?: Partial<CreateJob>;
  ai_context?: AITemplateContext;
}

export type JobTemplateListResponse = JobTemplate[];

export interface JobTemplateGroupedResponse {
  systemTemplates: JobTemplate[];
  companyTemplates: JobTemplate[];
  aiTemplates?: JobTemplate[]; // AI templates grouped separately
}

// ============================================================================
// AI Template Generation Types
// ============================================================================

/**
 * Request to generate job content from an AI template
 */
export interface GenerateFromTemplateRequest {
  template_id: string;
  company_id: string;
  job_title?: string; // Override title if provided
}

/**
 * Response from AI template generation
 */
export interface GenerateFromTemplateResponse {
  title?: string;
  description?: string;
  requirements?: string[];
  benefits?: string[];
  educational_qualifications?: EducationalQualification[];
}

/**
 * Helper type to check if a template is an AI template
 */
export const isAITemplate = (template: JobTemplate): boolean => {
  return template.template_type === "ai";
};

/**
 * Helper type to check if a template is a regular (non-AI) template
 */
export const isRegularTemplate = (template: JobTemplate): boolean => {
  return (
    template.template_type === "system" || template.template_type === "custom"
  );
};

// ============================================================================
// Enhanced Job Types (NEW - extending existing Job interface)
// ============================================================================

export interface EducationalQualification {
  id?: string;
  degree_level: "Graduate" | "Post-Graduate" | "Doctorate";
  preferred_iitian: boolean;
  preferred_institutions?: string;
  additional_notes?: string;
}

export interface EnhancedJobData extends Job {
  company_id?: string;
  is_featured: boolean;
  educational_qualifications?: EducationalQualification[];
  template_id?: string;
  draft_data?: string; // JSON string for draft state
}

export interface CreateEnhancedJobData extends CreateJob {
  company_id?: string;
  is_featured?: boolean;
  educational_qualifications?: Omit<EducationalQualification, "id">[];
}

export interface UpdateEnhancedJobData extends UpdateJob {
  company_id?: string;
  is_featured?: boolean;
  educational_qualifications?: EducationalQualification[];
}

// Enhanced Job Status Types
export type EnhancedJobStatus = "draft" | "active" | "archived" | "closed";

export interface UpdateJobStatusRequest {
  status: EnhancedJobStatus;
  notes?: string;
}

export interface JobStatusHistory {
  id: string;
  job_id: string;
  old_status: EnhancedJobStatus;
  new_status: EnhancedJobStatus;
  changed_by: string;
  changed_by_name?: string;
  notes?: string;
  changed_at: string;
}

// ============================================================================
// Candidate Stage Management Types (NEW)
// ============================================================================

export type CandidateStage =
  | "Applied"
  | "Resume Review"
  | "Shortlisted"
  | "Interview Scheduled"
  | "Interview Completed"
  | "Offer Extended"
  | "Hired"
  | "Rejected";

export interface CandidateApplication {
  id: string;
  job_id: string;
  candidate_id: string;
  resume_id?: string;
  current_stage: CandidateStage;
  stage_history: StageHistoryEntry[];
  applied_at: string;
  updated_at?: string;
}

export interface StageHistoryEntry {
  id: string;
  stage: CandidateStage;
  changed_by: string;
  changed_by_name?: string;
  notes?: string;
  changed_at: string;
}

export interface UpdateCandidateStageRequest {
  stage: CandidateStage;
  notes?: string;
}

export interface CandidateWithApplications {
  candidate: CandidateProfile;
  applications: CandidateApplication[];
}

export type CandidateApplicationListResponse = CandidateApplication[];

// ============================================================================
// Enhanced AI Generation Types (NEW)
// ============================================================================

export interface GenerateJobDescriptionRequestEnhanced {
  job_title: string;
  requirements?: string[];
  experience_level?: string;
}

export interface GenerateJobBenefitsRequest {
  job_title: string;
  level?: string;
  company_name?: string;
  company_id?: string;
  company_info?: string;
}

export interface GenerateJobRequirementsRequest {
  job_title: string;
  seniority_level?: string;
}

export interface SuggestSkillsRequest {
  job_title: string;
}

export interface AIGenerationResponse {
  content: string | string[];
  metadata?: {
    model: string;
    tokens_used: number;
    generation_time_ms: number;
  };
}

export interface GenerateCandidateEmailRequest {
  candidate_name: string;
  job_title: string;
  company_name: string;
  stage: CandidateStage;
  additional_context?: string;
}

export interface BiasAnalysisResponse {
  has_bias: boolean;
  biased_phrases: Array<{
    phrase: string;
    type: string;
    suggestion: string;
  }>;
  overall_score: number;
}

export interface ReadabilityScoreResponse {
  score: number;
  level: string;
  suggestions: string[];
}

// ============================================================================
// Enhanced Interview Scheduling Types (NEW)
// ============================================================================

export interface InterviewPanelMember {
  email: string;
  name?: string;
  role?: string;
  team_member_id?: string;
}

export interface CreateInterviewRequest {
  application_id: string;
  scheduled_date: string;
  duration_minutes: number;
  interview_type: string;
  panel_members: InterviewPanelMember[];
  notes?: string;
  create_google_meet: boolean;
  subject_id?: string;
}

export interface UpdateInterviewRequest {
  scheduled_date?: string;
  duration_minutes?: number;
  interview_type?: string;
  panel_members?: InterviewPanelMember[];
  notes?: string;
  subject_id?: string;
}

export interface GoogleMeetLinkResponse {
  meet_link: string;
  calendar_event_id: string;
  calendar_event_url: string;
}

export interface EnhancedInterview {
  id: string;
  application_id: string;
  scheduled_date: string;
  duration_minutes: number;
  interview_type: string;
  google_meet_link?: string;
  google_calendar_event_id?: string;
  panel_members: InterviewPanelMember[];
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at?: string;
}

export type EnhancedInterviewListResponse = EnhancedInterview[];

// ============================================================================
// Social Media Post Generation Types (NEW)
// ============================================================================

export type SocialPlatformDimension =
  | "instagram_square" // 1080x1080
  | "instagram_story" // 1080x1920
  | "linkedin_post" // 1200x627
  | "twitter_post" // 1200x675
  | "facebook_post"; // 1200x630

export interface GenerateSocialPostRequest {
  job_id: string;
  platform: SocialPlatformDimension;
  style?: "professional" | "modern" | "creative" | "minimalist";
}

export interface SocialPostResponse {
  image_url: string;
  download_url: string;
  platform: SocialPlatformDimension;
  dimensions: {
    width: number;
    height: number;
  };
}

// ============================================================================
// Job Analytics Enhancement Types (NEW)
// ============================================================================

export interface JobDetailedAnalytics extends JobAnalytics {
  applications_by_stage: Record<CandidateStage, number>;
  candidate_list: CandidateApplication[];
  status_history: JobStatusHistory[];
}

// ============================================================================
// Job Form State Types (NEW)
// ============================================================================

export interface JobFormState {
  // Basic Info
  title: string;
  description: string;
  company_id?: string;
  company_name?: string;
  location: string;

  // Compensation
  salary_min?: number;
  salary_max?: number;

  // Job Details
  job_type?: string;
  experience_level?: string;
  requirements: string[];
  benefits: string[];

  // Educational Qualifications
  educational_qualifications: EducationalQualification[];

  // Media
  company_logo_url?: string;
  job_image_url?: string;

  // Status
  status: EnhancedJobStatus;
  is_featured: boolean;

  // Template
  template_id?: string;

  // Metadata
  completeness_percentage: number;
  validation_errors: Record<string, string>;
}

// ============================================================================
// API Endpoint Constants
// ============================================================================

export const API_ENDPOINTS = {
  // Authentication
  AUTH_GOOGLE: "/api/auth/google",
  AUTH_LOGOUT: "/api/auth/logout",
  AUTH_ME: "/api/me",

  // Jobs
  JOBS: "/api/jobs",
  ADMIN_JOBS: "/api/admin/jobs",
  ADMIN_JOBS_ANALYTICS: "/api/admin/jobs/analytics",
  JOBS_VIEW: (id: string) => `/api/jobs/${id}/view`,
  JOBS_STATS: (id: string) => `/api/jobs/${id}/stats`,
  ADMIN_JOBS_BULK_UPDATE_STATUS: "/api/admin/jobs/bulk-update-status",
  ADMIN_JOBS_BULK_DELETE: "/api/admin/jobs/bulk-delete",

  // Profile
  PROFILE: "/api/profile",
  PROFILE_EXPERIENCE: "/api/profile/experience",
  PROFILE_EDUCATION: "/api/profile/education",

  // Resumes
  RESUMES: "/api/resumes",
  RESUMES_SCAN: (id: string) => `/api/resumes/${id}/scan`,
  RESUMES_STATUS: (id: string) => `/api/resumes/${id}/status`,
  RESUMES_DOWNLOAD: (id: string) => `/api/resumes/${id}/download`,
  RESUMES_RETRY: (id: string) => `/api/resumes/${id}/retry-processing`,
  ADMIN_RESUMES: "/api/admin/resumes",
  ADMIN_RESUMES_BULK_UPDATE_STATUS: "/api/admin/resumes/bulk-update-status",

  // Conversations
  CONVERSATIONS: "/api/conversations",
  ADMIN_CONVERSATIONS: (userId: string) => `/api/admin/conversations/${userId}`,
  CONVERSATION_READ: "/api/conversations/read",
  MESSAGES: "/api/messages",
  ATTACHMENTS: (filename: string) => `/api/attachments/${filename}`,

  // Avatar
  USER_AVATAR: "/api/user/avatar",
  AVATARS: (filename: string) => `/api/avatars/${filename}`,

  // Applications
  APPLICATIONS: "/api/applications",
  APPLICATION_DETAILS: (id: string) => `/api/applications/${id}`,
  APPLICATION_STATUS: (id: string) => `/api/applications/${id}/status`,
  ADMIN_APPLICATIONS_ANALYTICS: "/api/admin/applications/analytics",
  ADMIN_APPLICATIONS_BULK_UPDATE_STATUS:
    "/api/admin/applications/bulk-update-status",

  // Admin
  ADMIN_DASHBOARD_METRICS: "/api/admin/dashboard/metrics",
  ADMIN_SYSTEM_HEALTH: "/api/admin/system/health",
  ADMIN_ACTIVITY: "/api/admin/activity",
  ADMIN_CANDIDATES: "/api/admin/candidates",
  ADMIN_CANDIDATE_DETAILS: (id: string) => `/api/admin/candidates/${id}`,
  ADMIN_USERS: "/api/admin/users",
  ADMIN_USER_DETAILS: (id: string) => `/api/admin/users/${id}`,
  ADMIN_USER_TOGGLE_STATUS: (id: string) =>
    `/api/admin/users/${id}/toggle-status`,

  // Export
  ADMIN_EXPORT_JOBS: "/api/admin/export/jobs",
  ADMIN_EXPORT_APPLICATIONS: "/api/admin/export/applications",
  ADMIN_EXPORT_CANDIDATES: "/api/admin/export/candidates",

  // AI Generation
  AI_GENERATE_EMAIL: "/api/admin/candidates/ai/generate-email",
  AI_SUGGEST_RESPONSE: "/api/admin/ai/suggest-response",
  AI_GENERATE_JOB_DESCRIPTION: "/api/admin/ai/generate-job-description",
  AI_GENERATE_OFFER_LETTER: "/api/admin/ai/generate-offer-letter",
  AI_GENERATE_SOCIAL_IMAGES: "/api/admin/ai/generate-social-images",

  // Interviews
  ADMIN_INTERVIEWS: "/api/admin/interviews",
  ADMIN_INTERVIEWS_BY_CANDIDATE: (candidateId: string) =>
    `/api/admin/interviews/${candidateId}`,

  // Video Submissions
  APPLICATION_VIDEO: (applicationId: string) =>
    `/api/applications/${applicationId}/video`,
  ADMIN_APPLICATION_VIDEO_DOWNLOAD: (applicationId: string) =>
    `/api/admin/applications/${applicationId}/video/download`,

  // Companies (NEW)
  ADMIN_COMPANIES: "/api/admin/companies",
  ADMIN_COMPANY_DETAILS: (id: string) => `/api/admin/companies/${id}`,
  ADMIN_COMPANY_ASSETS: (companyId: string) =>
    `/api/admin/companies/${companyId}/assets`,
  ADMIN_COMPANY_ASSET_DELETE: (companyId: string, assetId: string) =>
    `/api/admin/companies/${companyId}/assets/${assetId}`,
  ADMIN_COMPANY_ASSET_SET_DEFAULT: (companyId: string, assetId: string) =>
    `/api/admin/companies/${companyId}/assets/${assetId}/set-default`,

  // Job Templates (NEW)
  ADMIN_JOB_TEMPLATES: "/api/admin/job-templates",
  ADMIN_JOB_TEMPLATE_DETAILS: (id: string) => `/api/admin/job-templates/${id}`,

  // Enhanced Job Management (NEW)
  ADMIN_JOB_STATUS: (jobId: string) => `/api/admin/jobs/${jobId}/status`,
  ADMIN_JOB_TOGGLE_FEATURED: (jobId: string) =>
    `/api/admin/jobs/${jobId}/toggle-featured`,
  ADMIN_JOB_DRAFT: "/api/admin/jobs/draft",
  ADMIN_JOB_DRAFT_DETAILS: (draftId: string) =>
    `/api/admin/jobs/draft/${draftId}`,
  ADMIN_JOB_DETAILED_ANALYTICS: (jobId: string) =>
    `/api/admin/jobs/${jobId}/detailed-analytics`,
  JOBS_FEATURED: "/api/jobs?featured=true",

  // Enhanced AI Generation (NEW)
  AI_GENERATE_JOB_DESCRIPTION_ENHANCED:
    "/api/admin/jobs/ai/generate-description",
  AI_GENERATE_JOB_BENEFITS: "/api/admin/jobs/ai/generate-benefits",
  AI_GENERATE_JOB_REQUIREMENTS: "/api/admin/jobs/ai/generate-requirements",
  AI_SUGGEST_SKILLS: "/api/admin/jobs/ai/suggest-skills",
  AI_GENERATE_CANDIDATE_EMAIL: "/api/admin/candidates/ai/generate-email",
  AI_ANALYZE_BIAS: "/api/admin/jobs/ai/analyze-bias",
  AI_READABILITY_SCORE: "/api/admin/jobs/ai/readability-score",
  AI_GENERATE_SOCIAL_POST: "/api/admin/jobs/ai/generate-social-post",

  // Candidate Management (NEW)
  ADMIN_JOB_CANDIDATES: (jobId: string) =>
    `/api/admin/jobs/${jobId}/candidates`,
  ADMIN_JOB_CANDIDATE_DETAILS: (jobId: string, candidateId: string) =>
    `/api/admin/jobs/${jobId}/candidates/${candidateId}`,
  ADMIN_JOB_CANDIDATE_STAGE: (jobId: string, candidateId: string) =>
    `/api/admin/jobs/${jobId}/candidates/${candidateId}/stage`,
  ADMIN_JOB_CANDIDATE_APPROVE: (jobId: string, candidateId: string) =>
    `/api/admin/jobs/${jobId}/candidates/${candidateId}/approve`,
  ADMIN_JOB_CANDIDATE_REJECT: (jobId: string, candidateId: string) =>
    `/api/admin/jobs/${jobId}/candidates/${candidateId}/reject`,
  ADMIN_JOB_CANDIDATE_EMAIL: (jobId: string, candidateId: string) =>
    `/api/admin/jobs/${jobId}/candidates/${candidateId}/email`,
  ADMIN_CANDIDATE_APPLICATIONS: (candidateId: string) =>
    `/api/admin/candidates/${candidateId}/applications`,
  ADMIN_CANDIDATES_BY_STAGE: "/api/admin/candidates",

  // Enhanced Interview Scheduling (NEW)
  ADMIN_INTERVIEWS_SCHEDULE: "/api/admin/interviews/schedule",
  ADMIN_INTERVIEW_DETAILS: (interviewId: string) =>
    `/api/admin/interviews/${interviewId}`,
  ADMIN_INTERVIEW_UPDATE: (interviewId: string) =>
    `/api/admin/interviews/${interviewId}`,
  ADMIN_INTERVIEW_CANCEL: (interviewId: string) =>
    `/api/admin/interviews/${interviewId}`,
  ADMIN_INTERVIEW_CREATE_GOOGLE_MEET:
    "/api/admin/interviews/create-google-meet",
  ADMIN_CANDIDATE_INTERVIEWS: (candidateId: string) =>
    `/api/admin/candidates/${candidateId}/interviews`,
  ADMIN_JOB_INTERVIEWS: (jobId: string) =>
    `/api/admin/jobs/${jobId}/interviews`,
} as const;

// ============================================================================
// Unified Job Creation System Types (Task 12.1)
// ============================================================================

// Job Template Types (removed duplicate - see line 1384)

// Enhanced Job Types
export interface EnhancedJobData extends Job {
  company_id?: string;
  is_featured: boolean;
  educational_qualifications?: EducationalQualification[];
  template_id?: string;
  draft_data?: string;
}

export interface UserVideo {
  id: string;
  user_id: string;
  filename: string;
  s3_url: string;
  duration_seconds: number;
  uploaded_at: string;
  video_source?: string;
  youtube_video_id?: string;
  youtube_thumbnail_url?: string;
  youtube_title?: string;
  youtube_description?: string;
}
export interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnail_url: string;
  published_at: string;
  duration: string;
  view_count?: number;
}
export interface LinkYouTubeVideoRequest {
  youtube_video_id: string;
  title?: string;
  description?: string;
  thumbnail_url?: string;
  duration_seconds?: number;
}

export interface ResumeAnalysis {
  name?: string;
  email?: string;
  phone?: string;
  skills: string[];
  experience: Array<{
    title: string;
    company: string;
    start?: string;
    end?: string;
    start_date?: string;
    end_date?: string | null;
    description?: string;
  }>;
  education: Array<{
    degree: string;
    institution: string;
    field?: string;
    field_of_study?: string;
    start?: string;
    end?: string;
    start_date?: string;
    end_date?: string | null;
    year?: string;
  }>;
}
export interface ResumeReview {
  id: string;
  status: string;
  score: number | null;
  ai_model: string;
  scanned_at: string | null;
  extracted_data: ResumeAnalysis | null;
  file_url: string;
  image_urls: string[];
}
